import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, Row, Spinner, Table } from "reactstrap";

import SectionCard from "components/SectionCard";
import EscolherArquivos from "components/EscolherArquivos";
import { Badge } from "components/ChecklistRow";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { useToast } from "hooks/toast";
import { listarTabelasReferencia, enviarTabelasReferencia, atualizarTabelaReferencia, removerTabelaReferencia, urlTabelaReferencia, mensagemErro } from "services/meuFreteApi";
import { ACCEPT, MAX_ARQUIVOS, validarAnexos, fmtTamanho } from "utils/anexosTabelaReferencia";
import { formatDate, formatDateTime } from "utils/formatDate";

// Aba "Tabelas de referência" do Cadastro do MeuFrete (PO1-3650, épico
// PO1-3359): a transportadora da Rede envia a tabela que quiser mostrar aos
// embarcadores (PDF/XLS/XLSX/CSV), controla se está visível, a vigência e uma
// observação, baixa e remove. Não vira FreteTabela: é só referência, não é
// usada para cálculo nem auditoria (termo da Rede). Para tabela estruturada,
// a transportadora usa "Tabelas enviadas" escolhendo a Rede como embarcador.

const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem" };
const labelStyle = { fontSize: "0.78rem", color: AUTOPILOT_COLORS.muted, marginBottom: 4 };
const stateBoxStyle = { display: "flex", alignItems: "center", gap: "0.6rem", padding: "1rem 0.25rem", color: AUTOPILOT_COLORS.muted, fontSize: "0.9rem" };
const ENVIO_VAZIO = { visivelEmbarcadores: true, vigenciaInicio: "", vigenciaFim: "", observacao: "" };

const vigenciaTexto = (t) => {
  if (!t.vigenciaInicio && !t.vigenciaFim) return "Sem vigência informada";
  return `${t.vigenciaInicio ? formatDate(t.vigenciaInicio) : "…"} a ${t.vigenciaFim ? formatDate(t.vigenciaFim) : "…"}`;
};

export default function AbaTabelasReferencia() {
  const { addToast } = useToast();
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erroCarga, setErroCarga] = useState("");
  const [arquivos, setArquivos] = useState([]);
  const [erroArquivos, setErroArquivos] = useState(null);
  const [envio, setEnvio] = useState(ENVIO_VAZIO);
  const [enviando, setEnviando] = useState(false);
  const [editar, setEditar] = useState(null); // { item, form }
  const [remover, setRemover] = useState(null);
  const [agindo, setAgindo] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErroCarga("");
    try {
      setItens(await listarTabelasReferencia());
    } catch (err) {
      setErroCarga(mensagemErro(err, "Não foi possível carregar as tabelas."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const escolher = (lista) => {
    const todos = arquivos.concat(Array.from(lista || [])).slice(0, MAX_ARQUIVOS + 1);
    setArquivos(todos);
    setErroArquivos(validarAnexos(todos));
  };

  const enviar = async () => {
    const e = arquivos.length ? validarAnexos(arquivos) : "Escolha pelo menos um arquivo.";
    setErroArquivos(e);
    if (e) return;
    if (envio.vigenciaInicio && envio.vigenciaFim && envio.vigenciaFim < envio.vigenciaInicio) {
      setErroArquivos("O fim da vigência não pode ser antes do início.");
      return;
    }
    setEnviando(true);
    try {
      const lista = await enviarTabelasReferencia(arquivos, envio);
      setItens(lista);
      setArquivos([]);
      setEnvio(ENVIO_VAZIO);
      addToast({ place: "tr", type: "success", title: "Tabela enviada", description: envio.visivelEmbarcadores ? "Já aparece para os embarcadores no Sourcing (selo \"Tem tabela\")." : "Guardada; marque \"visível\" quando quiser mostrar aos embarcadores." });
    } catch (err) {
      const d = (err && err.response && err.response.data) || {};
      if (d.field === "arquivos" || d.field === "tabelas") setErroArquivos(d.error);
      addToast({ place: "tr", type: "danger", title: "Não foi possível enviar", description: mensagemErro(err) });
    } finally {
      setEnviando(false);
    }
  };

  const alternarVisivel = async (t) => {
    setAgindo(true);
    try {
      const item = await atualizarTabelaReferencia(t.id, { visivelEmbarcadores: !t.visivelEmbarcadores });
      setItens((cur) => cur.map((x) => (x.id === t.id ? { ...x, ...(item || { visivelEmbarcadores: !t.visivelEmbarcadores }) } : x)));
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível atualizar", description: mensagemErro(err) });
    } finally {
      setAgindo(false);
    }
  };

  const salvarEdicao = async () => {
    if (!editar) return;
    const f = editar.form;
    if (f.vigenciaInicio && f.vigenciaFim && f.vigenciaFim < f.vigenciaInicio) {
      addToast({ place: "tr", type: "warning", title: "Vigência", description: "O fim da vigência não pode ser antes do início." });
      return;
    }
    setAgindo(true);
    try {
      const item = await atualizarTabelaReferencia(editar.item.id, {
        visivelEmbarcadores: f.visivelEmbarcadores,
        vigenciaInicio: f.vigenciaInicio || null,
        vigenciaFim: f.vigenciaFim || null,
        observacao: String(f.observacao || "").trim() || null,
      });
      setItens((cur) => cur.map((x) => (x.id === editar.item.id ? { ...x, ...(item || f) } : x)));
      setEditar(null);
      addToast({ place: "tr", type: "success", title: "Tabela atualizada" });
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível atualizar", description: mensagemErro(err) });
    } finally {
      setAgindo(false);
    }
  };

  const confirmarRemover = async () => {
    if (!remover) return;
    setAgindo(true);
    try {
      await removerTabelaReferencia(remover.id);
      setItens((cur) => cur.filter((x) => x.id !== remover.id));
      setRemover(null);
      addToast({ place: "tr", type: "success", title: "Tabela removida" });
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível remover", description: mensagemErro(err) });
    } finally {
      setAgindo(false);
    }
  };

  // URL pré-assinada (15 min): abre em nova aba, como em Tabelas enviadas.
  const baixar = async (t) => {
    try {
      const { url } = await urlTabelaReferencia(t.id);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível baixar", description: mensagemErro(err) });
    }
  };

  const setEnvioCampo = (campo) => (e) => {
    const valor = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setEnvio((cur) => ({ ...cur, [campo]: valor }));
  };
  const setEdicaoCampo = (campo) => (e) => {
    const valor = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setEditar((cur) => (cur ? { ...cur, form: { ...cur.form, [campo]: valor } } : cur));
  };

  return (
    <>
      <SectionCard eyebrow="Rede Everlog" title="Enviar tabela de referência" description={`PDF, XLS, XLSX ou CSV, até ${MAX_ARQUIVOS} arquivos de 10 MB. É só referência para o embarcador: não entra em cálculo nem em auditoria. Para uma tabela estruturada, use "Tabelas enviadas" escolhendo a Rede Everlog.`} style={{ marginBottom: "0.75rem" }}>
        <Row>
          <Col md="5">
            <FormGroup>
              <Label style={labelStyle}>Arquivos *</Label>
              <EscolherArquivos accept={ACCEPT} multiple onSelecionar={escolher} disabled={enviando} ariaLabel="Arquivos da tabela de referência" />
              {arquivos.length ? (
                <ul style={{ ...mutedStyle, paddingLeft: "1.1rem", marginTop: 6, marginBottom: 0 }}>
                  {arquivos.map((f, i) => (
                    <li key={`${f.name}-${i}`}>
                      {f.name} ({fmtTamanho(f.size)}){" "}
                      <Button color="link" size="sm" style={{ padding: 0, color: AUTOPILOT_COLORS.statusDanger }} onClick={() => { const todos = arquivos.filter((_, idx) => idx !== i); setArquivos(todos); setErroArquivos(validarAnexos(todos)); }}>
                        remover
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {erroArquivos ? <div style={{ color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.8rem", marginTop: 4 }} role="alert">{erroArquivos}</div> : null}
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label style={labelStyle}>Vigência de</Label>
              <Input type="date" value={envio.vigenciaInicio} onChange={setEnvioCampo("vigenciaInicio")} disabled={enviando} />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label style={labelStyle}>até</Label>
              <Input type="date" value={envio.vigenciaFim} onChange={setEnvioCampo("vigenciaFim")} disabled={enviando} />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label style={labelStyle}>Observação</Label>
              <Input value={envio.observacao} maxLength={500} onChange={setEnvioCampo("observacao")} disabled={enviando} placeholder="Ex.: fracionado Sul e Sudeste" />
            </FormGroup>
          </Col>
        </Row>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <FormGroup check style={{ marginBottom: 0 }}>
            <Label check style={{ color: AUTOPILOT_COLORS.text, fontSize: "0.88rem", cursor: "pointer" }}>
              <Input type="checkbox" checked={envio.visivelEmbarcadores} onChange={setEnvioCampo("visivelEmbarcadores")} disabled={enviando} /> Visível para embarcadores
              <span className="form-check-sign" />
            </Label>
          </FormGroup>
          <Button size="sm" color="info" onClick={enviar} disabled={enviando || !arquivos.length}>
            {enviando ? <Spinner size="sm" /> : "Enviar"}
          </Button>
        </div>
      </SectionCard>

      <SectionCard eyebrow="Rede Everlog" title={`Tabelas enviadas (${itens.length})`} description="Quem está visível aparece para os embarcadores no Sourcing com o selo “Tem tabela” e pode ser baixada por eles.">
        {loading ? (
          <div style={stateBoxStyle}><Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /><span>Carregando…</span></div>
        ) : erroCarga ? (
          <div style={stateBoxStyle} role="alert">{erroCarga} <Button size="sm" color="info" outline onClick={carregar}>Tentar de novo</Button></div>
        ) : !itens.length ? (
          <div style={stateBoxStyle}>Nenhuma tabela de referência enviada.</div>
        ) : (
          <Table size="sm" responsive hover className="hub-thead-accent">
            <thead className="text-primary">
              <tr><th>Arquivo</th><th>Vigência</th><th>Observação</th><th>Visível</th><th>Enviada em</th><th /></tr>
            </thead>
            <tbody>
              {itens.map((t) => (
                <tr key={t.id}>
                  <td>
                    <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>{t.nome}</div>
                    <div style={mutedStyle}>{String(t.formato || "").toUpperCase()} · {fmtTamanho(t.tamanho)}</div>
                  </td>
                  <td>{vigenciaTexto(t)}</td>
                  <td style={{ maxWidth: 260 }}>{t.observacao || "—"}</td>
                  <td>
                    <Button size="sm" color="link" style={{ padding: 0 }} onClick={() => alternarVisivel(t)} disabled={agindo} title="Clique para alternar">
                      {t.visivelEmbarcadores ? <Badge tipo="done">Visível</Badge> : <Badge tipo="lock">Oculta</Badge>}
                    </Button>
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>{t.criadoEm ? formatDateTime(t.criadoEm) : "—"}</td>
                  <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                    <Button size="sm" color="info" outline onClick={() => baixar(t)}>Baixar</Button>{" "}
                    <Button size="sm" color="info" outline onClick={() => setEditar({ item: t, form: { visivelEmbarcadores: Boolean(t.visivelEmbarcadores), vigenciaInicio: (t.vigenciaInicio || "").slice(0, 10), vigenciaFim: (t.vigenciaFim || "").slice(0, 10), observacao: t.observacao || "" } })}>Editar</Button>{" "}
                    <Button size="sm" color="danger" outline onClick={() => setRemover(t)}>Remover</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </SectionCard>

      <Modal isOpen={!!editar} toggle={agindo ? undefined : () => setEditar(null)} modalClassName="modal-align-top" fade={false}>
        {editar && (
          <>
            <ModalBody style={{ padding: "1.5rem" }}>
              <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, marginBottom: "0.35rem" }}>Editar tabela de referência</div>
              <div style={{ ...mutedStyle, marginBottom: "0.75rem" }}>{editar.item.nome}</div>
              <Row>
                <Col md="6"><FormGroup><Label style={labelStyle}>Vigência de</Label><Input type="date" value={editar.form.vigenciaInicio} onChange={setEdicaoCampo("vigenciaInicio")} /></FormGroup></Col>
                <Col md="6"><FormGroup><Label style={labelStyle}>até</Label><Input type="date" value={editar.form.vigenciaFim} onChange={setEdicaoCampo("vigenciaFim")} /></FormGroup></Col>
              </Row>
              <FormGroup>
                <Label style={labelStyle}>Observação</Label>
                <Input type="textarea" rows={2} maxLength={500} value={editar.form.observacao} onChange={setEdicaoCampo("observacao")} />
              </FormGroup>
              <FormGroup check>
                <Label check style={{ color: AUTOPILOT_COLORS.text, fontSize: "0.88rem", cursor: "pointer" }}>
                  <Input type="checkbox" checked={editar.form.visivelEmbarcadores} onChange={setEdicaoCampo("visivelEmbarcadores")} /> Visível para embarcadores
                  <span className="form-check-sign" />
                </Label>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" outline onClick={() => setEditar(null)} disabled={agindo}>Cancelar</Button>
              <Button color="info" onClick={salvarEdicao} disabled={agindo}>{agindo ? <Spinner size="sm" /> : "Salvar"}</Button>
            </ModalFooter>
          </>
        )}
      </Modal>

      <Modal isOpen={!!remover} toggle={agindo ? undefined : () => setRemover(null)} modalClassName="modal-align-top" fade={false}>
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, marginBottom: "0.35rem" }}>Remover tabela de referência</div>
          <div style={{ color: AUTOPILOT_COLORS.text, fontSize: "0.88rem" }}>
            {remover ? <>Remover <strong>{remover.nome}</strong>? Ela deixa de aparecer para os embarcadores.</> : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setRemover(null)} disabled={agindo}>Cancelar</Button>
          <Button color="danger" onClick={confirmarRemover} disabled={agindo}>{agindo ? <Spinner size="sm" /> : "Remover"}</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
